import { type JSX } from 'react';

import { I18nProvider } from './i18n';
import { ContentProvider } from './content/ContentProvider';
import { useSectionVisibility } from './content/homepage-model';
import { JTFooter } from './components/JTFooter';
import { JTHeader } from './components/JTHeader';
import {
  JTDownloadSection,
  JTGrowSection,
  JTHero,
  JTNewsSection,
  JTPartnerPortalSection,
  JTPharmacyOsSection,
  JTReferSection,
  JTServicesSection,
  JTSolutionsSection,
  JTSuperAppSection,
} from './components/sections';

/**
 * The Just Treated consumer homepage.
 *
 * Composition only. Each section owns its layout, and all copy and
 * destinations come from `content/homepage-content` and `routes`, so this file
 * stays a readable table of contents rather than becoming the whole website.
 *
 * The order matches the supplied mockup (the 2026-10 handoff) and should
 * not gain sections in between. Each section can be switched off from the
 * dashboard; none can be moved, because the sticky panels only stack in the
 * order they were designed in. Three of them — Pharmacy OS, Solutions and the Partner Portal — are
 * sticky panels that overlap as you scroll, which is why they are wrapped
 * rather than sitting flat in the flow.
 *
 * The marketplace does not appear here. The discovery strips ("Find care and
 * medicines near you", "Book a service", "Shop pharmacy and health") and the
 * PENNY Wellness "Earn PENNY for being active" rail were once added under the
 * hero, which turned the website's homepage into an app screen; the owner
 * rejected that on 2026-09-28. They live on their own routes (/search,
 * /categories, /storefronts, the offer pages) and in the storefront page's
 * wellness rail. Do not re-add them here without the owner's approval.
 *
 * `I18nProvider` wraps the whole page rather than the header alone, because
 * the header, the menu, the footer and the News chrome all read from it — and
 * because every page built on this shell later inherits the same provider by
 * sitting inside it. A provider scoped to one component is a provider that has
 * to move the first time a second component needs it.
 */
export function HomePage({
  initialContent,
}: {
  /** Lets tests supply published content without a network round trip. */
  readonly initialContent?: Readonly<Record<string, string>>;
} = {}): JSX.Element {
  return (
    <I18nProvider>
      <ContentProvider initial={initialContent}>
        {/* Keyboard users should not have to tab the header to reach the page. */}
        <a className="jt-skip" href="#main">
          Skip to main content
        </a>

        <JTHeader />

        <HomeSections />

        <JTFooter />
      </ContentProvider>
    </I18nProvider>
  );
}

/**
 * The sections an editor has left switched on, re-stacked.
 *
 * Pharmacy OS, Solutions and the Partner Portal are sticky panels; each later
 * one is pulled up a full viewport so it slides over the one before, and the
 * purple statement slides over the last of them. Switching one off must not
 * leave the next pulling itself up over the Explore cards, so each panel is told
 * whether anything above it is still there to overlap. The download section
 * does the same over the delivery-bag photograph, which belongs to Refer.
 *
 * The hero is not switchable: it carries the page's only `h1` and its largest
 * contentful paint.
 */
function HomeSections(): JSX.Element {
  const on = useSectionVisibility();

  return (
    <main id="main">
      <JTHero />
      {on.explore ? <JTServicesSection /> : null}
      {on.os ? <JTPharmacyOsSection /> : null}
      {on.solutions ? <JTSolutionsSection overlaps={on.os} /> : null}
      {on.portal ? <JTPartnerPortalSection overlaps={on.os || on.solutions} /> : null}
      {on.superapp ? <JTSuperAppSection overlaps={on.os || on.solutions || on.portal} /> : null}
      {on.grow ? <JTGrowSection /> : null}
      {on.refer ? <JTReferSection /> : null}
      {on.download ? <JTDownloadSection overlaps={on.refer} /> : null}
      {on.news ? <JTNewsSection /> : null}
    </main>
  );
}
